import { apiGet } from './api_v2';
import db from './db';
import { logError } from './logger';
import { readJson, storagePrefix, widgetGenerationActivityDismissed, writeJson } from './widgetGenerationNotifications';
import { timestampValue } from './widgetGenerationRun';

// Every derivation in this module is total over a missing chat: `agentChat` comes from
// live queries that legitimately hand back null/undefined for a render (row not
// replicated yet, purged row, re-keyed query mid-flight), so absent input must mean
// "no state" — never a throw. A render-time throw here has no boundary between it and
// unmounting the app.

const STORAGE_VERSION = 1;
const STALE_AFTER_MS = 20_000;
export const WIDGET_TURN_RECONCILE_INTERVAL_MS = 10_000;
// A pending turn normally clears within seconds of its run journaling in; one still
// unmatched after this long has a dead or unsynced chat behind it and gets probed.
const PENDING_TURN_PROBE_AFTER_SECONDS = 60;
// A pending turn whose chat row never appears at all is pure dead weight — it can
// render no banner (banners derive from the chat row) but still feeds null chats into
// every derivation keyed off the pending map.
const PENDING_TURN_ORPHAN_TTL_SECONDS = 300;
const BANNER_SEVERITY = Object.freeze({ failed: 5, ready: 4, working: 3, thinking: 2, message: 1 });
const TERMINAL_TURN_STATES = Object.freeze({ ready_to_publish: 'ready', failed: 'failed' });
const TERMINAL_BANNER_LABELS = Object.freeze({
  ready_to_publish: 'Latest preview is ready',
  failed: 'Failed to build',
});
const CHAT_STATUS_TURN_STATES = Object.freeze({ running: 'working', failed: 'failed' });
const pendingTurns = new Map();
const pendingTurnListeners = new Set();
const reconciliationInFlight = new Set();
let pendingTurnsSnapshot = Object.freeze([]);

const pendingTurnsStorageKey = () => `${storagePrefix()}:pending-turns`;
const pendingTurnKey = (workspaceId, agentChatId) => `${workspaceId}:${agentChatId}`;

function normalizePendingTurn(detail) {
  return Object.freeze({
    workspaceId: String(detail.workspaceId),
    agentChatId: String(detail.agentChatId),
    requestMessageId: String(detail.requestMessageId),
    startedAt: detail.startedAt,
  });
}

function commitPendingTurns() {
  pendingTurnsSnapshot = Object.freeze([...pendingTurns.values()]);
  pendingTurnListeners.forEach((listener) => listener());
  writeJson(pendingTurnsStorageKey(), { version: STORAGE_VERSION, pendingTurns: pendingTurnsSnapshot });
}

function restorePendingTurns() {
  const stored = readJson(pendingTurnsStorageKey(), null);
  if (stored?.version !== STORAGE_VERSION || !Array.isArray(stored.pendingTurns)) return;

  stored.pendingTurns.forEach((pendingTurn) => {
    if (!pendingTurn?.workspaceId || !pendingTurn?.agentChatId || !pendingTurn?.requestMessageId) return;

    const restoredTurn = normalizePendingTurn(pendingTurn);
    pendingTurns.set(pendingTurnKey(restoredTurn.workspaceId, restoredTurn.agentChatId), restoredTurn);
  });
  pendingTurnsSnapshot = Object.freeze([...pendingTurns.values()]);
}

function revisionValue(turn) {
  const revision = turn.builder_state?.revision;
  return [timestampValue(revision?.at || turn.updated_at), revision?.attempt || 0, revision?.sequence || 0];
}

function workingRevisionValue(revision) {
  const builderRevision = revision.builder_revision;
  return [
    timestampValue(builderRevision?.at || revision.updated_at),
    builderRevision?.attempt || 0,
    builderRevision?.sequence || 0,
  ];
}

function acceptsIncomingProjection(current, incoming, revisionFor) {
  if (!current) return true;
  if (current.id !== incoming.id) {
    if (current.created_at !== incoming.created_at) return incoming.created_at > current.created_at;
    return incoming.updated_at > current.updated_at;
  }

  const currentRevision = revisionFor(current);
  const incomingRevision = revisionFor(incoming);
  for (let index = 0; index < currentRevision.length; index += 1) {
    if (incomingRevision[index] !== currentRevision[index]) return incomingRevision[index] > currentRevision[index];
  }
  return true;
}

// A newer run always wins; within a run the builder_state revision tuple orders
// payloads across Cable, journal, and polling deliveries.
function acceptsIncomingTurn(currentTurn, incomingTurn) {
  return acceptsIncomingProjection(currentTurn, incomingTurn, revisionValue);
}

function acceptsIncomingWorkingRevision(currentRevision, incomingRevision) {
  return acceptsIncomingProjection(currentRevision, incomingRevision, workingRevisionValue);
}

// Full chat snapshots arrive through API responses, journal hydration, and DM
// hydration. Their generation_turn is merged with the same ordering rule as live
// turn events so a delayed snapshot cannot move the builder backwards.
export function mergeAgentChatWithGenerationTurn(currentAgentChat, incomingAgentChat) {
  const currentTurn = currentAgentChat?.generation_turn;
  const incomingTurn = incomingAgentChat?.generation_turn;
  const generationTurn = incomingTurn && acceptsIncomingTurn(currentTurn, incomingTurn) ? incomingTurn : currentTurn;
  const currentWorkingRevision = currentAgentChat?.working_revision;
  const incomingWorkingRevision = incomingAgentChat?.working_revision;
  const workingRevision =
    incomingWorkingRevision && acceptsIncomingWorkingRevision(currentWorkingRevision, incomingWorkingRevision)
      ? incomingWorkingRevision
      : currentWorkingRevision;

  const merged = {
    ...currentAgentChat,
    ...incomingAgentChat,
    generation_turn: generationTurn || null,
  };
  if (workingRevision) merged.working_revision = workingRevision;
  else delete merged.working_revision;
  return merged;
}

export async function storeAgentChatWithGenerationTurn(incomingAgentChat) {
  const key = [incomingAgentChat.workspace_id, incomingAgentChat.id];
  await db.transaction('rw', db.agent_chats, async () => {
    const currentAgentChat = await db.agent_chats.get(key);
    await db.agent_chats.put(mergeAgentChatWithGenerationTurn(currentAgentChat, incomingAgentChat));
  });
}

// Server reads are the authority for whether a chat still exists. Every caller uses
// this path so a Dexie miss cannot be mistaken for a deletion, and successful reads
// always rehydrate the local generation turn with the normal merge semantics.
export async function fetchAgentChatWithGenerationTurn(agentChatId) {
  const response = await apiGet(`/agent_chats/${agentChatId}`);
  if (response.status === 404) return null;

  const data = await response.json().catch(() => null);
  const agentChat = data?.data?.agent_chat;
  if (!response.ok || !data?.ok || !agentChat) throw new Error('Could not load widget chat.');

  await storeAgentChatWithGenerationTurn(agentChat);
  return agentChat;
}

export function subscribeToPendingWidgetGenerationTurns(listener) {
  pendingTurnListeners.add(listener);
  return () => pendingTurnListeners.delete(listener);
}

export function pendingWidgetGenerationTurnsSnapshot() {
  return pendingTurnsSnapshot;
}

export function beginPendingWidgetGenerationTurn(detail) {
  // The create-message response always returns the persisted request id. Refuse to
  // create an activity that cannot be matched and dismissed deterministically.
  if (!detail?.workspaceId || !detail?.agentChatId || !detail?.requestMessageId) return;

  const pendingTurn = normalizePendingTurn(detail);
  pendingTurns.set(pendingTurnKey(pendingTurn.workspaceId, pendingTurn.agentChatId), pendingTurn);
  commitPendingTurns();
}

export function clearPendingWidgetGenerationTurn(workspaceId, agentChatId) {
  if (!workspaceId || !agentChatId) return;
  if (!pendingTurns.delete(pendingTurnKey(String(workspaceId), String(agentChatId)))) return;

  commitPendingTurns();
}

export function pendingWidgetGenerationTurnAcceptsRun(pendingTurn, turn) {
  return Boolean(pendingTurn?.requestMessageId && turn?.request_message_id && pendingTurn.requestMessageId === turn.request_message_id);
}

// The turn-only write path: the server builds the turn
// (WidgetGenerationRun#turn_payload) and every delivery lands here verbatim.
export async function applyWidgetGenerationTurn(turn) {
  return applyWidgetGenerationState({ generation_turn: turn });
}

export async function applyWidgetGenerationState(state, identity = {}) {
  const turn = state?.generation_turn;
  const workingRevision = state?.working_revision;
  if (!turn && !workingRevision) return false;

  const workspaceId = turn?.workspace_id || identity.workspaceId;
  const agentChatId = turn?.agent_chat_id || identity.agentChatId;
  if (!workspaceId || !agentChatId) return false;

  let turnApplied = false;
  let workingRevisionApplied = false;
  await db.agent_chats
    .where('[workspace_id+id]')
    .equals([workspaceId, agentChatId])
    .modify((agentChat) => {
      if (turn && acceptsIncomingTurn(agentChat.generation_turn, turn)) {
        agentChat.generation_turn = turn;
        turnApplied = true;
      }
      if (
        workingRevision &&
        acceptsIncomingWorkingRevision(agentChat.working_revision, workingRevision)
      ) {
        agentChat.working_revision = workingRevision;
        workingRevisionApplied = true;
      }
    });
  if (!turnApplied && !workingRevisionApplied) return false;

  // Clear the pending "Thinking…" entry only when the store accepted the turn,
  // and use the resolved ids: a chat-less turn payload omits agent_chat_id.
  if (turnApplied) {
    const pendingTurn = pendingTurns.get(pendingTurnKey(workspaceId, agentChatId));
    if (pendingTurn && pendingWidgetGenerationTurnAcceptsRun(pendingTurn, turn)) {
      clearPendingWidgetGenerationTurn(workspaceId, agentChatId);
    }
  }
  return true;
}

function widgetGenerationTurnIsStale(agentChat) {
  const turn = agentChat?.generation_turn;
  return Boolean(turn?.builder_state?.active && Date.now() - timestampValue(turn.updated_at) > STALE_AFTER_MS);
}

export async function reconcileWidgetGenerationTurn(agentChat) {
  if (!widgetGenerationTurnIsStale(agentChat)) return;

  const turn = agentChat.generation_turn;
  const key = pendingTurnKey(agentChat.workspace_id, agentChat.id);
  if (reconciliationInFlight.has(key)) return;

  reconciliationInFlight.add(key);
  try {
    const response = await apiGet(`/widget_generation_runs/${turn.id}`);
    if (response.status === 404) {
      await db.agent_chats
        .where('[workspace_id+id]')
        .equals([agentChat.workspace_id, agentChat.id])
        .modify((storedAgentChat) => {
          if (storedAgentChat.generation_turn?.id === turn.id) storedAgentChat.generation_turn = null;
        });
      return;
    }

    const data = await response.json();
    if (response.ok && data.ok && (data.data?.generation_turn || data.data?.working_revision)) {
      await applyWidgetGenerationState(data.data, {
        workspaceId: agentChat.workspace_id,
        agentChatId: agentChat.id,
      });
    }
  } catch (error) {
    logError('widget-generation-turns', 'Failed to reconcile widget generation turn', error);
  } finally {
    reconciliationInFlight.delete(key);
  }
}

// Nothing server-side retires a pending turn: it clears only when its run's turn is
// applied on this client. A submission whose run never journals in (server died, chat
// purged) would otherwise show "Thinking…" forever. While the chat still claims to be
// running with no matching turn, refresh it from the API so the real outcome flows
// through the normal precedence walk; a pending turn whose chat row never appears at
// all is dropped after a generous window.
export async function reconcilePendingWidgetGenerationTurn(pendingTurn) {
  const ageSeconds = Math.floor(Date.now() / 1000) - (pendingTurn.startedAt || 0);
  if (ageSeconds < PENDING_TURN_PROBE_AFTER_SECONDS) return;

  const key = `pending:${pendingTurnKey(pendingTurn.workspaceId, pendingTurn.agentChatId)}`;
  if (reconciliationInFlight.has(key)) return;

  reconciliationInFlight.add(key);
  try {
    const agentChat = await db.agent_chats.get([pendingTurn.workspaceId, pendingTurn.agentChatId]);
    if (!agentChat) {
      if (ageSeconds > PENDING_TURN_ORPHAN_TTL_SECONDS) {
        clearPendingWidgetGenerationTurn(pendingTurn.workspaceId, pendingTurn.agentChatId);
      }
      return;
    }

    const turn = agentChat.generation_turn;
    if (agentChat.status !== 'running' || (turn && pendingWidgetGenerationTurnAcceptsRun(pendingTurn, turn))) return;

    const refreshedAgentChat = await fetchAgentChatWithGenerationTurn(pendingTurn.agentChatId);
    if (!refreshedAgentChat) {
      await db.agent_chats.delete([pendingTurn.workspaceId, pendingTurn.agentChatId]);
      clearPendingWidgetGenerationTurn(pendingTurn.workspaceId, pendingTurn.agentChatId);
    }
  } catch (error) {
    logError('widget-generation-turns', 'Failed to reconcile pending widget generation turn', error);
  } finally {
    reconciliationInFlight.delete(key);
  }
}

// One reconciler for every surface, owned by the module: chats whose turn is active get
// their staleness probe from a single interval that reads Dexie fresh each tick. It is
// time-triggered because a missed terminal event is precisely the case where no new
// event arrives to re-trigger a component effect — and it keys on the turn, not the
// banner, so dismissing a banner (attention) never stops reconciliation (state).
let reconcilerIntervalId = null;

export async function reconcileActiveWidgetGenerationTurns() {
  const agentChats = await db.agent_chats.where('kind').equals('widget').toArray();
  agentChats.forEach((agentChat) => {
    if (agentChat.generation_turn?.builder_state?.active) reconcileWidgetGenerationTurn(agentChat);
  });
  pendingTurnsSnapshot.forEach((pendingTurn) => reconcilePendingWidgetGenerationTurn(pendingTurn));
}

export function ensureWidgetGenerationTurnReconciler() {
  if (reconcilerIntervalId != null) return;

  reconcilerIntervalId = window.setInterval(reconcileActiveWidgetGenerationTurns, WIDGET_TURN_RECONCILE_INTERVAL_MS);
}

// The one precedence walk over a chat's generation state. State indicators and
// banners share it; opening the builder does not mutate or acknowledge this state.
export function widgetGenerationTurnState(agentChat, pendingTurn) {
  const turn = agentChat?.generation_turn;
  const pendingRunMatches = Boolean(pendingTurn && turn && pendingWidgetGenerationTurnAcceptsRun(pendingTurn, turn));

  // A pending request is the current cycle. A retained turn belongs to the previous
  // request until its request id matches, so it must never mask the new outcome.
  if (pendingTurn && !pendingRunMatches) {
    if (agentChat?.status === 'running') return 'thinking';
    if (agentChat?.status === 'failed') return 'failed';
    if (agentChat?.status === 'finished') return 'message';
  }
  if (turn?.builder_state?.active) return 'working';

  return TERMINAL_TURN_STATES[turn?.builder_state?.state] || CHAT_STATUS_TURN_STATES[agentChat?.status] || null;
}

export function widgetGenerationActivityCycleId(agentChat, pendingTurn, turn = agentChat?.generation_turn) {
  if (turn?.request_message_id) return `request:${turn.request_message_id}`;
  if (pendingTurn?.requestMessageId) return `request:${pendingTurn.requestMessageId}`;
  if (turn?.id) return `run:${turn.id}`;
  return null;
}

function activityDetails(agentChat, turn, pendingTurn, kind, label) {
  return {
    kind,
    label,
    severity: BANNER_SEVERITY[kind] || 0,
    recency:
      (turn ? turn.occurred_at || turn.builder_state?.started_at || turn.created_at : null) ||
      pendingTurn?.startedAt ||
      agentChat.timestamp ||
      0,
    cycleId: widgetGenerationActivityCycleId(agentChat, pendingTurn, turn),
    runId: turn?.id || null,
    widgetId: turn?.widget_id || agentChat.widget_id || null,
    agentChatId: agentChat.id,
    workspaceId: agentChat.workspace_id,
    name: turn?.name || agentChat.title || null,
  };
}

// Every chat contributes one current activity cycle, including a closed cycle. The
// hook selects the newest cycle per widget before filtering closed or dismissed ones,
// so an older duplicate chat can never re-surface behind a newer cycle.
export function widgetGenerationActivityForChat(agentChat, pendingTurn) {
  if (!agentChat) return null;

  const turn = agentChat.generation_turn;
  const pendingRunMatches = Boolean(pendingTurn && turn && pendingWidgetGenerationTurnAcceptsRun(pendingTurn, turn));

  if (pendingTurn && !pendingRunMatches) {
    if (agentChat.status === 'running') return activityDetails(agentChat, null, pendingTurn, 'thinking', 'Thinking…');
    if (agentChat.status === 'failed') return activityDetails(agentChat, null, pendingTurn, 'failed', 'Failed to build');
    if (agentChat.status === 'finished') return activityDetails(agentChat, null, pendingTurn, 'message', 'New reply');
  }

  if (turn?.builder_state?.active) {
    return activityDetails(agentChat, turn, pendingTurn, 'working', turn.builder_state.label || 'Working…');
  }

  const builderState = turn?.builder_state?.state;
  const terminalKind = TERMINAL_TURN_STATES[builderState];
  if (terminalKind) {
    return activityDetails(agentChat, turn, pendingTurn, terminalKind, TERMINAL_BANNER_LABELS[builderState]);
  }

  // Publishing ends the cycle. Unpublishing does not revive it; only a later request
  // creates a new cycle.
  if (['published', 'unpublished'].includes(builderState)) {
    return activityDetails(agentChat, turn, pendingTurn, null, null);
  }
  if (builderState === 'superseded') {
    return activityDetails(agentChat, turn, pendingTurn, 'message', 'Generation superseded');
  }

  return activityDetails(agentChat, turn, pendingTurn, null, null);
}

export function widgetGenerationBannerForActivity(activity) {
  if (!activity?.kind || !activity.cycleId || widgetGenerationActivityDismissed(activity.cycleId)) return null;

  return activity;
}

export function widgetGenerationBannersForActivities(activities) {
  const currentActivities = new Map();

  activities.filter(Boolean).forEach((activity) => {
    const identity = activity.widgetId ? `widget:${activity.widgetId}` : `chat:${activity.agentChatId}`;
    const current = currentActivities.get(identity);
    if (!current || activity.recency > current.recency || (activity.recency === current.recency && activity.severity > current.severity)) {
      currentActivities.set(identity, activity);
    }
  });

  return [...currentActivities.values()]
    .map(widgetGenerationBannerForActivity)
    .filter(Boolean)
    .sort((left, right) => right.severity - left.severity || right.recency - left.recency);
}

export function widgetGenerationBannerForChat(agentChat, pendingTurn) {
  return widgetGenerationBannerForActivity(widgetGenerationActivityForChat(agentChat, pendingTurn));
}

restorePendingTurns();
